import { useEffect, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  MultiSelect,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Table,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconArrowLeft, IconExternalLink } from '@tabler/icons-react';
import { trpc } from '../../lib/trpc';
import { FlowBuilder } from '../../components/flow-builder/FlowBuilder';
import { canRepresentInBuilder } from '../../components/flow-builder/flow-model';

/**
 * Form editor: metadata + a visual builder over the Flow JSON (with a raw-JSON
 * escape hatch), push to Meta, publish lifecycle, test sends, and the responses
 * table. The builder reads/writes flowJson; advanced flows open in JSON mode.
 */

/** The 8 official Meta WhatsApp Flow categories (Flows API `categories` enum). */
type FlowCategory =
  | 'SIGN_UP'
  | 'SIGN_IN'
  | 'APPOINTMENT_BOOKING'
  | 'LEAD_GENERATION'
  | 'CONTACT_US'
  | 'CUSTOMER_SUPPORT'
  | 'SURVEY'
  | 'OTHER';
const FLOW_CATEGORY_OPTIONS: { value: FlowCategory; label: string }[] = [
  { value: 'SIGN_UP', label: 'Sign up' },
  { value: 'SIGN_IN', label: 'Sign in' },
  { value: 'APPOINTMENT_BOOKING', label: 'Appointment booking' },
  { value: 'LEAD_GENERATION', label: 'Lead generation' },
  { value: 'CONTACT_US', label: 'Contact us' },
  { value: 'CUSTOMER_SUPPORT', label: 'Customer support' },
  { value: 'SURVEY', label: 'Survey' },
  { value: 'OTHER', label: 'Other' },
];

function FormEditor() {
  const { uid } = Route.useParams();
  const utils = trpc.useUtils();
  const form = trpc.forms.get.useQuery({ uid });
  const responses = trpc.forms.responses.useQuery({ uid });

  const [name, setName] = useState('');
  const [ctaText, setCtaText] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [firstScreen, setFirstScreen] = useState('');
  const [flowJson, setFlowJson] = useState('');
  const [sendTo, setSendTo] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [responseLabelId, setResponseLabelId] = useState<string | null>(null);
  const [autofillContact, setAutofillContact] = useState(true);
  const [categories, setCategories] = useState<FlowCategory[]>([]);
  const [view, setView] = useState<'builder' | 'json'>('builder');
  const [endpointEnabled, setEndpointEnabled] = useState(false);
  const [endpointConfigJson, setEndpointConfigJson] = useState('');
  const labels = trpc.contacts.labels.list.useQuery();

  useEffect(() => {
    if (!form.data) return;
    setName(form.data.name);
    setCtaText(form.data.ctaText);
    setBodyText(form.data.bodyText);
    setFirstScreen(form.data.firstScreen);
    setFlowJson(form.data.flowJson);
    setView(canRepresentInBuilder(form.data.flowJson) ? 'builder' : 'json');
    setResponseLabelId(form.data.responseLabelId ? String(form.data.responseLabelId) : null);
    setAutofillContact(form.data.autofillContact);
    setEndpointEnabled(form.data.endpointEnabled ?? false);
    setEndpointConfigJson(form.data.endpointConfigJson ?? '');
    try {
      setCategories(JSON.parse(form.data.categoriesJson ?? '[]') as FlowCategory[]);
    } catch {
      setCategories([]);
    }
  }, [form.data]);

  useEffect(() => {
    if (!flowJson) return;
    try {
      JSON.parse(flowJson);
      setJsonError(null);
    } catch (e) {
      setJsonError(e instanceof Error ? e.message : 'Invalid JSON');
    }
  }, [flowJson]);

  const invalidate = () => {
    void utils.forms.get.invalidate({ uid });
    void utils.forms.list.invalidate();
  };
  const onError = (e: { message: string }) =>
    notifications.show({ message: e.message, color: 'red' });

  const update = trpc.forms.update.useMutation({
    onSuccess: () => {
      invalidate();
      notifications.show({ message: 'Saved', color: 'green' });
    },
    onError,
  });
  const push = trpc.forms.pushToMeta.useMutation({
    onSuccess: (r) => {
      invalidate();
      const errs = (r.validationErrors ?? []) as unknown[];
      notifications.show({
        message:
          errs.length > 0
            ? `Pushed — Meta reported ${errs.length} validation issue(s)`
            : 'Pushed to Meta',
        color: errs.length > 0 ? 'yellow' : 'green',
      });
    },
    onError,
  });
  const publish = trpc.forms.publish.useMutation({
    onSuccess: () => {
      invalidate();
      notifications.show({ message: 'Published — the form is live', color: 'green' });
    },
    onError,
  });
  const deprecate = trpc.forms.deprecate.useMutation({
    onSuccess: () => {
      invalidate();
      notifications.show({ message: 'Deprecated', color: 'yellow' });
    },
    onError,
  });
  const send = trpc.forms.send.useMutation({
    onSuccess: () => notifications.show({ message: 'Form sent', color: 'green' }),
    onError,
  });
  const refresh = trpc.forms.refresh.useMutation({
    onSuccess: () => invalidate(),
    onError,
  });
  const preview = trpc.forms.previewUrl.useQuery({ uid }, { enabled: !!form.data?.metaFlowId });

  if (form.isPending) return <Loader />;
  if (!form.data) return <Text>Form not found.</Text>;

  const f = form.data;
  const validationErrors: unknown[] = f.validationJson
    ? ((JSON.parse(f.validationJson) as unknown[]) ?? [])
    : [];
  const saveDisabled = !!jsonError || !name.trim() || !ctaText.trim() || !bodyText.trim();
  const locked = f.status === 'PUBLISHED' || f.status === 'DEPRECATED';

  const updatePayload = () => ({
    uid,
    name: name.trim(),
    ctaText: ctaText.trim(),
    bodyText: bodyText.trim(),
    firstScreen: firstScreen.trim(),
    flowJson,
    responseLabelId: responseLabelId ? Number(responseLabelId) : null,
    autofillContact,
    categories: categories.length > 0 ? categories : undefined,
    endpointEnabled,
    endpointConfigJson: endpointConfigJson.trim() ? endpointConfigJson : null,
  });

  const saveDraft = () => update.mutate(updatePayload());

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group gap="xs">
          <Anchor component={Link} to="/vendor/forms">
            <IconArrowLeft size={18} />
          </Anchor>
          <Title order={2}>{f.name}</Title>
          <Badge
            color={
              f.status === 'PUBLISHED' ? 'green' : f.status === 'DEPRECATED' ? 'gray' : 'yellow'
            }
            variant="light"
          >
            {f.status === 'draft' ? 'Not pushed' : f.status}
          </Badge>
        </Group>
        <Group gap="xs">
          {preview.data?.url && (
            <Button
              variant="default"
              component="a"
              href={preview.data.url}
              target="_blank"
              rel="noreferrer noopener"
              rightSection={<IconExternalLink size={14} />}
            >
              Preview
            </Button>
          )}
          {f.metaFlowId && (
            <Button
              variant="default"
              onClick={() => refresh.mutate({ uid })}
              loading={refresh.isPending}
            >
              Refresh status
            </Button>
          )}
        </Group>
      </Group>

      <Tabs defaultValue="design">
        <Tabs.List>
          <Tabs.Tab value="design">Design</Tabs.Tab>
          <Tabs.Tab value="responses">Responses ({f.responseCount})</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="design" pt="md">
          <Stack gap="sm">
            {validationErrors.length > 0 && (
              <Alert
                color="yellow"
                icon={<IconAlertTriangle size={16} />}
                title="Meta validation issues"
              >
                <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: 12 }}>
                  {JSON.stringify(validationErrors, null, 2)}
                </pre>
              </Alert>
            )}
            {locked && (
              <Alert color="blue">
                Published flows can&apos;t change their JSON. To redesign, create a new form (Meta
                deprecates rather than edits published flows).
              </Alert>
            )}
            <Group grow>
              <TextInput
                label="Name"
                value={name}
                onChange={(e) => setName(e.currentTarget.value)}
              />
              <TextInput
                label="Button text (CTA)"
                maxLength={30}
                value={ctaText}
                onChange={(e) => setCtaText(e.currentTarget.value)}
              />
              <TextInput
                label="Entry screen id"
                value={firstScreen}
                onChange={(e) => setFirstScreen(e.currentTarget.value)}
              />
            </Group>
            <MultiSelect
              label="Categories"
              description="Meta Flow categories — sent to Meta when you push. SIGN_IN needs a dynamic endpoint (coming later)."
              data={FLOW_CATEGORY_OPTIONS}
              value={categories}
              onChange={(v) => setCategories(v as FlowCategory[])}
              searchable
              clearable
            />
            <Textarea
              label="Message body (sent with the form button)"
              autosize
              minRows={2}
              maxLength={1024}
              value={bodyText}
              onChange={(e) => setBodyText(e.currentTarget.value)}
            />
            <Stack gap="xs">
              <Group justify="space-between" align="center">
                <Text size="sm" fw={600}>
                  Form design
                </Text>
                <SegmentedControl
                  size="xs"
                  value={view}
                  onChange={(v) => setView(v as 'builder' | 'json')}
                  data={[
                    { value: 'builder', label: 'Builder' },
                    { value: 'json', label: 'JSON' },
                  ]}
                />
              </Group>
              {view === 'builder' ? (
                <FlowBuilder
                  value={flowJson}
                  onChange={setFlowJson}
                  onFirstScreenChange={setFirstScreen}
                  disabled={locked}
                />
              ) : (
                <Textarea
                  label="Flow JSON"
                  description="The raw Meta Flow JSON document — the builder reads and writes this."
                  autosize
                  minRows={14}
                  maxRows={30}
                  styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
                  value={flowJson}
                  error={jsonError}
                  onChange={(e) => setFlowJson(e.currentTarget.value)}
                  disabled={locked}
                />
              )}
            </Stack>
            <Card withBorder padding="sm">
              <Stack gap="xs">
                <Text size="sm" fw={600}>
                  When submitted
                </Text>
                <Text size="xs" c="dimmed">
                  Actions run automatically each time a contact completes this form.
                </Text>
                <Switch
                  label="Auto-fill empty contact details from answers (name, email)"
                  checked={autofillContact}
                  onChange={(e) => setAutofillContact(e.currentTarget.checked)}
                />
                <Select
                  label="Apply label to the contact"
                  placeholder="No label"
                  clearable
                  data={(labels.data ?? []).map((l) => ({ value: String(l.id), label: l.title }))}
                  value={responseLabelId}
                  onChange={setResponseLabelId}
                />
                <Text size="xs" c="dimmed">
                  Submissions also post to your outgoing webhook (event
                  <Text span ff="monospace">
                    {' '}
                    flow.response
                  </Text>
                  ) and drop a note on the contact.
                </Text>
              </Stack>
            </Card>
            <Card withBorder padding="sm">
              <Stack gap="xs">
                <Text size="sm" fw={600}>
                  Advanced — dynamic endpoint
                </Text>
                <Switch
                  label="Dynamic flow (data-exchange endpoint)"
                  description="Let screens fetch live data, validate server-side, or branch. Requires an endpoint key registered for your WhatsApp number."
                  checked={endpointEnabled}
                  onChange={(e) => setEndpointEnabled(e.currentTarget.checked)}
                  disabled={locked}
                />
                {endpointEnabled && (
                  <Textarea
                    label="Endpoint config (JSON)"
                    description="Maps screens to built-in providers: static_data, dynamic_dropdown, server_validation, conditional_next, complete_capture."
                    autosize
                    minRows={4}
                    maxRows={16}
                    styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
                    value={endpointConfigJson}
                    onChange={(e) => setEndpointConfigJson(e.currentTarget.value)}
                    disabled={locked}
                    placeholder={
                      '{\n  "screens": {\n    "WELCOME": {\n      "init": { "provider": "static_data", "params": { "data": {} } }\n    }\n  }\n}'
                    }
                  />
                )}
              </Stack>
            </Card>
            <Group justify="space-between">
              <Group gap="xs">
                <Button
                  variant="default"
                  onClick={saveDraft}
                  loading={update.isPending}
                  disabled={saveDisabled}
                >
                  Save
                </Button>
                {!locked && (
                  <Button
                    onClick={async () => {
                      await update.mutateAsync(updatePayload());
                      push.mutate({ uid });
                    }}
                    loading={push.isPending}
                    disabled={saveDisabled}
                  >
                    {f.metaFlowId ? 'Save & sync to Meta' : 'Push to Meta'}
                  </Button>
                )}
                {f.metaFlowId && f.status !== 'PUBLISHED' && f.status !== 'DEPRECATED' && (
                  <Button
                    color="green"
                    onClick={() => {
                      if (
                        confirm('Publishing is one-way — the JSON locks afterwards. Publish now?')
                      ) {
                        publish.mutate({ uid });
                      }
                    }}
                    loading={publish.isPending}
                  >
                    Publish
                  </Button>
                )}
                {f.status === 'PUBLISHED' && (
                  <Button
                    color="red"
                    variant="light"
                    onClick={() => {
                      if (confirm('Deprecate this form? It can no longer be sent.')) {
                        deprecate.mutate({ uid });
                      }
                    }}
                    loading={deprecate.isPending}
                  >
                    Deprecate
                  </Button>
                )}
              </Group>
              <Group gap="xs" align="flex-end">
                <TextInput
                  label="Send to (phone with country code)"
                  placeholder="+91 98765 43210"
                  value={sendTo}
                  onChange={(e) => setSendTo(e.currentTarget.value)}
                />
                <Button
                  variant="default"
                  disabled={!f.metaFlowId || sendTo.trim().length < 5}
                  loading={send.isPending}
                  onClick={() => send.mutate({ uid, to: sendTo.trim() })}
                >
                  {f.status === 'PUBLISHED' ? 'Send' : 'Send test (draft)'}
                </Button>
              </Group>
            </Group>
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="responses" pt="md">
          <ResponsesTable
            responses={(responses.data ?? []).map((r) => ({
              uid: r.uid,
              receivedAt: String(r.receivedAt),
              responseJson: r.responseJson,
            }))}
          />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

function ResponsesTable({
  responses,
}: {
  responses: Array<{ uid: string; receivedAt: string; responseJson: string }>;
}) {
  const rows = responses.map((r) => {
    let parsed: Record<string, unknown> = {};
    try {
      parsed = JSON.parse(r.responseJson) as Record<string, unknown>;
    } catch {
      parsed = { raw: r.responseJson };
    }
    const { flow_token: _t, ...answers } = parsed;
    return { uid: r.uid, receivedAt: r.receivedAt, answers };
  });

  // Stable column set: union of answer keys across responses.
  const columns = Array.from(new Set(rows.flatMap((r) => Object.keys(r.answers))));

  const exportCsv = () => {
    const header = ['received_at', ...columns];
    const lines = [header.join(',')];
    for (const r of rows) {
      const cells = [
        new Date(r.receivedAt).toISOString(),
        ...columns.map((c) => {
          const v = r.answers[c];
          const s = v === undefined || v === null ? '' : String(v);
          return `"${s.replace(/"/g, '""')}"`;
        }),
      ];
      lines.push(cells.join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'form-responses.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (rows.length === 0) {
    return (
      <Card withBorder>
        <Text c="dimmed" size="sm">
          No responses yet — submissions appear here the moment a customer completes the form.
        </Text>
      </Card>
    );
  }

  return (
    <Stack gap="sm">
      <Group justify="flex-end">
        <Button variant="default" size="xs" onClick={exportCsv}>
          Export CSV
        </Button>
      </Group>
      <Table striped withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Received</Table.Th>
            {columns.map((c) => (
              <Table.Th key={c}>{c}</Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((r) => (
            <Table.Tr key={r.uid}>
              <Table.Td>{new Date(r.receivedAt).toLocaleString()}</Table.Td>
              {columns.map((c) => (
                <Table.Td key={c}>
                  {r.answers[c] === undefined ? '—' : String(r.answers[c])}
                </Table.Td>
              ))}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Stack>
  );
}

export const Route = createFileRoute('/vendor/forms/$uid')({ component: FormEditor });
